import { useState, useEffect } from "react";
import axios from "axios";
import { ClipboardList, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pnlCls = (v) => (v >= 0 ? "text-green-400" : "text-red-400");

export default function OptionsScannerLogPage() {
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [exitInputs, setExitInputs] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/scanner-options/log`).then(r => setTrades(r.data.trades || []));
    axios.get(`${API}/scanner-options/log/stats`).then(r => setStats(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const closeTrade = async (id) => {
    const price = parseFloat(exitInputs[id]);
    if (isNaN(price) || price < 0) return alert("Enter a valid exit option price");
    setBusy(true);
    try { await axios.post(`${API}/scanner-options/log/${id}/close`, { exit_option_price: price, exit_reason: "manual" }); load(); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const del = async (id) => { await axios.delete(`${API}/scanner-options/log/${id}`); load(); };

  const open = trades.filter(t => t.status === "open");
  const closed = trades.filter(t => t.status === "closed");
  const ov = stats?.overall;

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white px-3 py-4 max-w-[1600px] mx-auto" data-testid="options-scanner-log-page">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="w-5 h-5 text-purple-400" />
        <h1 className="text-xl font-bold">Options Scanner Log</h1>
        <span className="text-[10px] text-gray-500">paper trades logged from the Overview scanner · win/loss tracked by score band</span>
      </div>
      <div className="text-[10px] text-rose-300/80 mb-3">Paper trading only — no real orders. P&L per contract (×100 shares).</div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="log-stats-cards">
        {[["Win rate", ov?.win_rate_pct != null ? `${ov.win_rate_pct}%` : "—", "log-stat-winrate"],
          ["Closed (W/L)", ov ? `${ov.closed} (${ov.wins}/${ov.losses})` : "—", "log-stat-closed"],
          ["Total P&L", ov ? `$${ov.total_pnl}` : "—", "log-stat-pnl"],
          ["Avg win / loss", ov ? `${ov.avg_win_pct ?? "—"}% / ${ov.avg_loss_pct ?? "—"}%` : "—", "log-stat-avg"],
          ["Open trades", stats?.open_count ?? "—", "log-stat-open"]].map(([k, v, tid]) => (
          <div key={k} className="border border-slate-800 bg-slate-900/40 rounded p-3" data-testid={tid}>
            <div className="text-[10px] uppercase text-gray-500">{k}</div>
            <div className="text-xl font-bold">{v}</div>
          </div>
        ))}
      </div>

      {/* By score band + direction */}
      {stats && (Object.keys(stats.by_score_band || {}).length > 0 || Object.keys(stats.by_direction || {}).length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          <div className="border border-purple-900/50 rounded p-3" data-testid="log-band-stats">
            <div className="text-[10px] uppercase text-purple-400 font-bold mb-2">Win rate by score band</div>
            {Object.entries(stats.by_score_band).map(([band, s]) => (
              <div key={band} className="flex justify-between text-xs py-1 border-b border-slate-800/50" data-testid={`log-band-${band.replace(/ /g, "-")}`}>
                <span>{band}</span>
                <span>{s.wins}W / {s.losses}L · <b className={s.win_rate_pct >= 50 ? "text-green-400" : "text-red-400"}>{s.win_rate_pct}%</b> · exp {s.expectancy_pct}%</span>
              </div>
            ))}
          </div>
          <div className="border border-slate-800 rounded p-3" data-testid="log-direction-stats">
            <div className="text-[10px] uppercase text-gray-400 font-bold mb-2">Win rate by direction</div>
            {Object.entries(stats.by_direction).map(([d, s]) => (
              <div key={d} className="flex justify-between text-xs py-1 border-b border-slate-800/50">
                <span className="capitalize">{d}s</span>
                <span>{s.wins}W / {s.losses}L · <b className={s.win_rate_pct >= 50 ? "text-green-400" : "text-red-400"}>{s.win_rate_pct}%</b></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open trades */}
      <div className="mb-4">
        <div className="text-sm font-bold mb-1">Open paper trades ({open.length})</div>
        {open.length === 0 && <div className="text-xs text-gray-500 border border-slate-800 rounded p-3" data-testid="log-no-open">No open trades — log one from the Options Scanner setup card on the Overview page.</div>}
        {open.length > 0 && (
          <div className="overflow-x-auto border border-slate-800 rounded">
            <table className="w-full text-xs" data-testid="log-open-table">
              <thead><tr className="text-gray-500 bg-slate-900/60 border-b border-slate-800">
                {["Ticker", "Dir", "Expiry", "Strike", "Entry $", "Score", "Band", "Δ", "Entered", "Exit price", "", ""].map((h, i) => <th key={i} className="px-2 py-1.5 text-left">{h}</th>)}
              </tr></thead>
              <tbody>
                {open.map(t => (
                  <tr key={t.id} className="border-b border-slate-800/60" data-testid={`log-open-row-${t.ticker}`}>
                    <td className="px-2 py-1.5 font-semibold">{t.ticker}</td>
                    <td className={`px-2 py-1.5 ${t.direction === "call" ? "text-green-400" : "text-red-400"}`}>{t.direction}</td>
                    <td className="px-2 py-1.5">{t.expiration}</td>
                    <td className="px-2 py-1.5">${t.strike}</td>
                    <td className="px-2 py-1.5">${t.entry_option_price}</td>
                    <td className="px-2 py-1.5">{t.combined_score}</td>
                    <td className="px-2 py-1.5">{t.score_band}</td>
                    <td className="px-2 py-1.5">{t.delta}</td>
                    <td className="px-2 py-1.5 text-gray-500">{t.entry_time?.slice(0, 10)}</td>
                    <td className="px-2 py-1.5">
                      <input data-testid={`log-exit-input-${t.ticker}`} type="number" step="0.01" placeholder="0.00"
                        value={exitInputs[t.id] || ""} onChange={e => setExitInputs({ ...exitInputs, [t.id]: e.target.value })}
                        className="w-20 bg-slate-900 border border-slate-700 rounded px-1.5 py-1 text-white" />
                    </td>
                    <td className="px-2 py-1.5">
                      <button data-testid={`log-close-btn-${t.ticker}`} onClick={() => closeTrade(t.id)} disabled={busy}
                        className="px-2 py-1 rounded bg-purple-800/70 text-purple-100 text-[10px] font-bold hover:bg-purple-700 disabled:opacity-50">Close</button>
                    </td>
                    <td className="px-2 py-1.5"><button data-testid={`log-delete-btn-${t.ticker}`} onClick={() => del(t.id)} className="text-gray-600 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Closed trades */}
      <div>
        <div className="text-sm font-bold mb-1">Closed trades ({closed.length})</div>
        {closed.length === 0 && <div className="text-xs text-gray-500 border border-slate-800 rounded p-3" data-testid="log-no-closed">No closed trades yet.</div>}
        {closed.length > 0 && (
          <div className="overflow-x-auto border border-slate-800 rounded">
            <table className="w-full text-xs" data-testid="log-closed-table">
              <thead><tr className="text-gray-500 bg-slate-900/60 border-b border-slate-800">
                {["Ticker", "Dir", "Expiry", "Strike", "Entry $", "Exit $", "P&L", "P&L %", "W/L", "Band", "Score", "Closed", ""].map((h, i) => <th key={i} className="px-2 py-1.5 text-left">{h}</th>)}
              </tr></thead>
              <tbody>
                {closed.map(t => (
                  <tr key={t.id} className="border-b border-slate-800/60" data-testid={`log-closed-row-${t.ticker}`}>
                    <td className="px-2 py-1.5 font-semibold">{t.ticker}</td>
                    <td className={`px-2 py-1.5 ${t.direction === "call" ? "text-green-400" : "text-red-400"}`}>{t.direction}</td>
                    <td className="px-2 py-1.5">{t.expiration}</td>
                    <td className="px-2 py-1.5">${t.strike}</td>
                    <td className="px-2 py-1.5">${t.entry_option_price}</td>
                    <td className="px-2 py-1.5">${t.exit_option_price}</td>
                    <td className={`px-2 py-1.5 font-bold ${pnlCls(t.pnl)}`}>${t.pnl}</td>
                    <td className={`px-2 py-1.5 ${pnlCls(t.pnl_pct)}`}>{t.pnl_pct}%</td>
                    <td className={`px-2 py-1.5 font-bold ${t.win ? "text-green-400" : "text-red-400"}`}>{t.win ? "WIN" : "LOSS"}</td>
                    <td className="px-2 py-1.5">{t.score_band}</td>
                    <td className="px-2 py-1.5">{t.combined_score}</td>
                    <td className="px-2 py-1.5 text-gray-500">{t.exit_time?.slice(0, 10)}</td>
                    <td className="px-2 py-1.5"><button onClick={() => del(t.id)} className="text-gray-600 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
